import 'leaflet/dist/leaflet.css';

import { Box, Grid2, Typography } from '@mui/material';
import { getEmergingAlertHighlights, getSiteRatings, selectEmergingAlertHighlights, selectIfAlertHighlightsLoading, selectIfSiteRatingsLoading } from '../store/slices/emergingAlertsSlice';
import { useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AdnocLoadingProgress from '../components/shared/AdnocLoadingProgress';
import { AppDispatch } from '../store/store';
import DeviationRatingLegends from '../components/shared/DeviationRatingLegends';
import Metric from '../components/home/Metric';
import OpenStreetMapWithMarkers from '../components/maps/OpenStreetMapWithMarkers';
import { PILOT_USER } from '../constants/PilotUser';
import { RESPONSIVE_HEIGHTS } from '../utils/ViewportUtils';
import { SHOW_HIGHLIGHS_ONLY } from '../constants/AlertHlightsViewings';
import { ViewportContext } from '../contexts/viewportContext';
import getGreetings from '../utils/Greetings';

const VP_MIN_HEIGHT_MAPS = 650;
const VP_MIN_MAP_ZOOM_LEVEL = 6.7;

const MAP_TOP_COORDS = { lat: 70.2, lng: -149.2 }
const MAP_BOTTOM_COORDS = { lat: 70.2, lng: -149.2 }

const getZoomLevelByHeight = (height: number) => {
	if (height >= RESPONSIVE_HEIGHTS['4K'])
		return 8.5;
	if (height >= RESPONSIVE_HEIGHTS['2K'])
		return 8;

	const heightRatio = height / VP_MIN_HEIGHT_MAPS;
	const factor = heightRatio > 1 ? heightRatio * 0.5 : 0;
	return VP_MIN_MAP_ZOOM_LEVEL + factor;
};

function DeviationRadar() {

	const vpContext = useContext(ViewportContext);
	const zoomLevel = getZoomLevelByHeight(vpContext?.height!)

	const dispatch = useDispatch<AppDispatch>();

	const isAlertHighlightsLoading = useSelector(selectIfAlertHighlightsLoading);
	const highlights = useSelector(selectEmergingAlertHighlights);
	const showOnlyHighlights = useMemo(() => {
		return highlights.filter((s: any) => !SHOW_HIGHLIGHS_ONLY.includes(s.heading_name));
	}, [highlights])

	const isSiteRatingsLoading = useSelector(selectIfSiteRatingsLoading);

	useEffect(() => {
		if (isAlertHighlightsLoading === null)
			dispatch(getEmergingAlertHighlights())

		if (isSiteRatingsLoading === null)
			dispatch(getSiteRatings())
	}, [dispatch, isAlertHighlightsLoading, isSiteRatingsLoading])

	return (
		<Grid2
			sx={{
				width: '100vw',
				height: '125vh',
				boxSizing: 'border-box',
				display: 'flex',
				flexDirection: 'row',
				position: 'relative',
				zIndex: 1,
			}}
		>
			<Grid2
				size={{ lg: 3 }}
				sx={{
					height: 'inherit',
					boxSizing: 'border-box',
					position: 'absolute',
					zIndex: 2,
					top: 'max(64px, 6vh)',
					left: 0,
					padding: { md: 2, xl: vpContext?.is4k ? 4 : 2 },
					background: 'linear-gradient(to right, #F2F7F9 2.5%, #E7F6FF00 100%)',
				}}
			>
				<Box sx={{
					paddingY: 1,
					display: "flex",
					flexDirection: 'column',
					alignContent: 'center',
					background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) -7.34%, rgba(255, 255, 255, 0.52) 50%)'
				}}>
					<Typography component={'div'}
						variant="h4"
						sx={{
							height: '100%',
							borderLeft: '0.5vw solid #41D9E1',
							paddingX: 1,
							textTransform: 'capitalize',
						}}>
						{getGreetings()} {PILOT_USER.first_name}
					</Typography>
				</Box>
				<Box sx={{
					marginTop: { lg: 5 },
					display: 'grid',
					rowGap: 2,
				}}>
					{
						isSiteRatingsLoading && <AdnocLoadingProgress></AdnocLoadingProgress>
					}
					{
						showOnlyHighlights.length > 0 && showOnlyHighlights.map((ah: any, index: number) => {
							return <Metric key={index} label={ah.heading_name} text={ah.description}></Metric>
						})
					}
				</Box>
			</Grid2>
			<Grid2 sx={{
				width: '100vw',
				height: '125vh',
				position: 'relative',
				zIndex: 1,
				top: 64,
			}}>
				{/* <MapWithLocationMarkers zoomLevel={zoomLevel}
					center={{ lat: (MAP_TOP_COORDS.lat + MAP_BOTTOM_COORDS.lat) / 2, lng: 54 }}></MapWithLocationMarkers> */}
				<OpenStreetMapWithMarkers zoomLevel={zoomLevel}
					center={{ lat: (MAP_TOP_COORDS.lat + MAP_BOTTOM_COORDS.lat) / 2, lng: MAP_TOP_COORDS.lng }}></OpenStreetMapWithMarkers>
			</Grid2>
			<DeviationRatingLegends></DeviationRatingLegends>
		</Grid2>
	);
}

export default DeviationRadar;
